import React from "react";
import { useTranslation } from "react-i18next";
import "./OpenLiftProofs.css";

/**
 * OPENlift — section 07 : preuves et validation terrain.
 *
 * Toutes les données numériques ci-dessous sont des déclarations
 * communiquées par UniTech Engineering, NON auditées dans ce livrable.
 * Avant publication : documenter cohorte, période, bases de comparaison,
 * calculs, limites, consentements clients et validation R&D/commerciale.
 * Les chiffres ne doivent jamais être décrits comme universels ou garantis.
 */
const COPY = {
  fr: {
    eyebrow: "DÉVELOPPEMENT ET PREUVES TERRAIN",
    titleA: "Une solution développée,",
    titleB: "testée et éprouvée sur le terrain.",
    intro: "Chaque intégration de contrôleur est développée puis éprouvée dans des dizaines d’installations avant validation. OPENlift est aujourd’hui utilisé sur des centaines d’ascenseurs.",
    validationEyebrow: "NOTRE MÉTHODE",
    validationTitle: "La fiabilité se construit avant le déploiement.",
    validationText: "Nous adaptons chaque intégration aux réalités des contrôleurs et aux configurations rencontrées sur le terrain.",
    stages: [
      { num: "01", title: "Développer", text: "Étudier les contrôleurs, les interfaces disponibles et les données exploitables." },
      { num: "02", title: "Tester sur le terrain", text: "Confronter l’intégration à des dizaines d’installations et à leurs particularités." },
      { num: "03", title: "Valider par configuration", text: "Confirmer les fonctions disponibles avant de proposer le déploiement." }
    ],
    evidenceTag: "RETOUR D'EXPÉRIENCE",
    deployedNumber: "Des centaines",
    deployedLabel: "d’ascenseurs équipés et suivis avec OPENlift",
    deployedDetail: "Déploiements déclarés par UniTech Engineering ; volume exact et périmètre à confirmer pour publication.",
    resultsEyebrow: "STATISTIQUES COMMUNIQUÉES",
    resultsTitle: "Des résultats observés sur des parcs clients.",
    metrics: [
      { value: "+30 %", label: "Disponibilité améliorée", detail: "" },
      { value: "−80 %", label: "Arrêts non planifiés", detail: "" },
      { value: "−20 %", label: "Coûts réduits", detail: "" },
      { value: "+30 %", label: "Interventions évitées", detail: "" }
    ],
    cautionTitle: "À propos de ces résultats",
    caution: "Source déclarée : statistiques internes de parcs clients UniTech. Ces valeurs ne constituent pas une garantie ni une étude indépendante. Périodes, tailles d’échantillon, méthodes de calcul et autorisations de diffusion restent à documenter et à valider avant mise en production.",
    limitations: "Les niveaux d’amélioration dépendent des équipements, des configurations et des conditions d’exploitation."
  },
  en: {
    eyebrow: "ENGINEERING AND FIELD EVIDENCE",
    titleA: "Developed, tested",
    titleB: "and proven in the field.",
    intro: "Each controller integration is developed and tested across dozens of installations before validation. OPENlift is currently used on hundreds of elevators.",
    validationEyebrow: "OUR METHOD",
    validationTitle: "Reliability starts before rollout.",
    validationText: "Every integration is adapted to real controller families, available interfaces, and field configurations.",
    stages: [
      { num: "01", title: "Develop", text: "Study controllers, available interfaces and usable data." },
      { num: "02", title: "Test in the field", text: "Assess the integration across dozens of installations and specific conditions." },
      { num: "03", title: "Validate by configuration", text: "Confirm available functions before rollout is proposed." }
    ],
    evidenceTag: "FIELD EXPERIENCE",
    deployedNumber: "Hundreds",
    deployedLabel: "of elevators equipped and monitored with OPENlift",
    deployedDetail: "Deployments reported by UniTech Engineering; exact volume and scope to be documented before publication.",
    resultsEyebrow: "INTERNAL RESULTS REPORTED",
    resultsTitle: "Reported results from customer fleets.",
    metrics: [
      { value: "+15 to +30%", label: "Improved availability", detail: "" },
      { value: "−80%", label: "Unplanned stops", detail: "" },
      { value: "−20%", label: "Lower costs", detail: "" },
      { value: "+35%", label: "Avoided interventions", detail: "" }
    ],
    cautionTitle: "About these figures",
    caution: "Declared source: internal customer-fleet statistics supplied by UniTech. These figures are neither a guarantee nor an independently audited study. Period, sample size, calculation method, and publication permission must be documented and approved before production release.",
    limitations: "Outcomes depend on equipment, configuration and operating conditions."
  }
};

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="m5 12 4 4L19 6" />
    </svg>
  );
}

export default function OpenLiftProofs() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase().startsWith("en") ? "en" : "fr";
  const copy = COPY[lang];

  return (
    <section className="ol-proof" id="ol-proofs" aria-labelledby="ol-proof-title">
      <div className="ol-proof__container">
        <header className="ol-proof__header">
          <p className="ol-proof__eyebrow">{copy.eyebrow}</p>
          <h2 className="ol-proof__title" id="ol-proof-title">
            <span>{copy.titleA}</span>
            <span className="ol-proof__titleAccent">{copy.titleB}</span>
          </h2>
          <p className="ol-proof__intro">{copy.intro}</p>
        </header>

        <div className="ol-proof__story">
          <div className="ol-proof__method">
            <p className="ol-proof__smallLabel">{copy.validationEyebrow}</p>
            <h3 className="ol-proof__methodTitle">{copy.validationTitle}</h3>
            <p className="ol-proof__methodText">{copy.validationText}</p>
            <ol className="ol-proof__steps">
              {copy.stages.map((stage) => (
                <li key={stage.num} className="ol-proof__step">
                  <span className="ol-proof__stepNumber" aria-hidden="true">{stage.num}</span>
                  <span className="ol-proof__stepContent">
                    <strong>{stage.title}</strong>
                    <span>{stage.text}</span>
                  </span>
                  <span className="ol-proof__stepCheck"><CheckIcon /></span>
                </li>
              ))}
            </ol>
          </div>

          <aside className="ol-proof__deployment" aria-label={copy.evidenceTag}>
            <p className="ol-proof__deploymentEyebrow">{copy.evidenceTag}</p>
            <span className="ol-proof__deploymentLine" aria-hidden="true" />
            <p className="ol-proof__deploymentNumber">{copy.deployedNumber}</p>
            <p className="ol-proof__deploymentLabel">{copy.deployedLabel}</p>
          </aside>
        </div>

        <div className="ol-proof__results">
          <div className="ol-proof__resultsHead">
            <div>
              <p className="ol-proof__eyebrow ol-proof__resultsEyebrow">{copy.resultsEyebrow}</p>
              <h3 className="ol-proof__resultsTitle">{copy.resultsTitle}</h3>
            </div>
          </div>

          <div className="ol-proof__metrics">
            {copy.metrics.map((metric) => (
              <article className="ol-proof__metric" key={metric.label}>
                <p className="ol-proof__metricValue">{metric.value}</p>
                <h4 className="ol-proof__metricName">{metric.label}</h4>
                <p className="ol-proof__metricDetail">{metric.detail}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
